import React, { useEffect, useState, useCallback } from "react";
import { X, Send, Loader2, CheckCircle2, AlertCircle, Clock } from "lucide-react";
import { Button } from "../ui/button";
import { marketingApi } from "../../lib/api";
import { toast } from "sonner";

function StatusBadge({ status }) {
  const map = {
    draft: { c: "bg-slate-100 text-slate-600", label: "Draft" },
    sending: { c: "bg-blue-100 text-blue-700", label: "Sending" },
    sent: { c: "bg-emerald-100 text-emerald-700", label: "Sent" },
    failed: { c: "bg-red-100 text-red-700", label: "Failed" },
  };
  const s = map[status] || map.draft;
  return <span className={`inline-flex h-6 px-2.5 items-center rounded-full text-[11px] font-semibold ${s.c}`}>{s.label}</span>;
}

export default function CampaignDetail({ campaignId, open, onClose, smtpConfigured, onChanged }) {
  const [camp, setCamp] = useState(null);
  const [sending, setSending] = useState(false);

  const load = useCallback(async () => {
    if (!campaignId) return;
    try {
      const c = await marketingApi.getCampaign(campaignId);
      setCamp(c);
    } catch (e) {
      // ignore
    }
  }, [campaignId]);

  useEffect(() => {
    if (open) load();
  }, [open, load]);

  // Poll while sending
  useEffect(() => {
    if (!open || camp?.status !== "sending") return;
    const t = setInterval(load, 1500);
    return () => clearInterval(t);
  }, [open, camp?.status, load]);

  useEffect(() => {
    if (camp && onChanged) onChanged();
  }, [camp?.status, camp?.processedCount]); // eslint-disable-line

  if (!open || !camp) return null;

  const doSend = async () => {
    if (!smtpConfigured) return toast.error("Connect your email (SMTP) in Company Settings to send.");
    if (!window.confirm("Send this campaign now?")) return;
    setSending(true);
    try {
      await marketingApi.sendCampaign(camp.id);
      toast.success("Campaign is sending…");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to send.");
    } finally {
      setSending(false);
    }
  };

  const total = camp.recipientCount || 0;
  const processed = camp.processedCount || 0;
  const pct = total ? Math.round((processed / total) * 100) : 0;
  const recipients = camp.recipients || [];

  return (
    <div className="fixed inset-0 z-[110] flex justify-end" data-testid="campaign-detail">
      <div className="absolute inset-0 bg-slate-900/40" onClick={onClose} />
      <div className="relative w-full max-w-[560px] bg-white h-full overflow-y-auto shadow-2xl">
        <div className="sticky top-0 bg-white border-b border-slate-200 px-5 py-3.5 flex items-center justify-between">
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <h2 className="text-[15px] font-semibold text-slate-900 truncate">{camp.name}</h2>
              <StatusBadge status={camp.status} />
            </div>
            <p className="text-[12px] text-slate-500 truncate">{camp.subject}</p>
          </div>
          <button onClick={onClose} data-testid="campaign-detail-close" className="h-8 w-8 rounded-md hover:bg-slate-100 flex items-center justify-center shrink-0">
            <X className="h-4.5 w-4.5 text-slate-500" />
          </button>
        </div>

        <div className="p-5 space-y-5">
          {/* Stats */}
          <div className="grid grid-cols-3 gap-3">
            {[
              { label: "Recipients", value: total, icon: Clock, c: "text-slate-700" },
              { label: "Delivered", value: camp.deliveredCount || 0, icon: CheckCircle2, c: "text-emerald-600" },
              { label: "Failed", value: camp.failedCount || 0, icon: AlertCircle, c: "text-red-600" },
            ].map((s) => (
              <div key={s.label} className="rounded-xl border border-slate-200 p-3" data-testid={`campaign-stat-${s.label.toLowerCase()}`}>
                <div className="flex items-center gap-1.5 text-[11px] text-slate-500"><s.icon className="h-3.5 w-3.5" /> {s.label}</div>
                <div className={`text-[22px] font-semibold mt-0.5 ${s.c}`}>{s.value}</div>
              </div>
            ))}
          </div>

          {camp.status === "sending" && (
            <div data-testid="campaign-progress">
              <div className="flex justify-between text-[12px] text-slate-500 mb-1"><span>Sending…</span><span>{processed}/{total}</span></div>
              <div className="h-2 rounded-full bg-slate-100 overflow-hidden">
                <div className="h-full bg-blue-600 transition-all" style={{ width: `${pct}%` }} />
              </div>
            </div>
          )}

          {camp.status === "draft" && (
            <div className="rounded-lg bg-slate-50 border border-slate-200 px-4 py-3 flex items-center justify-between">
              <span className="text-[13px] text-slate-600">This campaign hasn't been sent yet.</span>
              <Button onClick={doSend} disabled={sending} data-testid="campaign-detail-send" className="h-8 gap-1.5 bg-blue-600 hover:bg-blue-700 text-white">
                {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} Send now
              </Button>
            </div>
          )}

          {/* Recipient list */}
          {recipients.length > 0 && (
            <div>
              <div className="text-[12px] font-semibold text-slate-700 mb-2">Delivery log ({recipients.length})</div>
              <div className="rounded-xl border border-slate-200 divide-y divide-slate-100 max-h-[360px] overflow-y-auto" data-testid="campaign-recipients">
                {recipients.map((r, i) => (
                  <div key={i} className="flex items-center justify-between px-3.5 py-2 text-[13px]">
                    <div className="min-w-0">
                      <div className="font-medium text-slate-800 truncate">{r.name || r.email}</div>
                      <div className="text-[11.5px] text-slate-400 truncate">{r.email}</div>
                    </div>
                    {r.status === "sent" ? (
                      <span className="inline-flex items-center gap-1 text-[11.5px] text-emerald-600"><CheckCircle2 className="h-3.5 w-3.5" /> Sent</span>
                    ) : (
                      <span className="inline-flex items-center gap-1 text-[11.5px] text-red-600" title={r.error || ""}><AlertCircle className="h-3.5 w-3.5" /> Failed</span>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
